import { useResizeObserver } from "@react-hookz/web";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";

import { notEmpty } from "shared/lib/utils";

import { appBarFixedHeaderHeightAtom } from "./atoms";

/**
 * Report the height of a fixed-position header (e.g. the home mini header) so that
 * `useAppBarStickyHeightValue` can account for it while the app bar container is translated away.
 * @param isVisible Whether the fixed header is currently shown on screen
 */
export const useAppBarSetFixedHeaderHeight = <T extends HTMLElement>(isVisible: boolean) => {
  const setFixedHeaderHeight = useSetAtom(appBarFixedHeaderHeightAtom);
  const [element, setElement] = useState<null | T>(null);

  const handleResize = useCallback(() => {
    setFixedHeaderHeight(isVisible && element ? (element.getBoundingClientRect().height ?? 0) : 0);
  }, [element, isVisible, setFixedHeaderHeight]);

  const reference = useCallback((node: null | T) => {
    if (!notEmpty(node)) return;
    setElement(node);
  }, []);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  useResizeObserver(element, _ => {
    handleResize();
  });

  // Reset when the fixed header is unmounted, e.g. navigating away from the home page
  useEffect(() => () => setFixedHeaderHeight(0), [setFixedHeaderHeight]);

  return {
    reference,
  };
};
